/*
 * ap-alarm.js — die kompakte Benachrichtigungs-Zeile der Produktseite.
 *
 * Vier Anlaesse, ein Skript:
 *
 *   price    POST /apps/tyre-search/pricewatch  { ean, email, kind:'price' }
 *   stock    POST /apps/tyre-search/pricewatch  { ean, email, kind:'stock' }
 *   qty      POST /apps/tyre-search/pricewatch  { ean, email, kind:'qty', wantedQty }
 *   anfrage  POST /apps/tyre-search/mengenanfrage { ean, menge, email, telefon, nachricht }
 *
 * KEIN HALBER BAU (Vertrag der App, proxy.pricewatch.tsx): zuerst EIN GET auf
 * den Alarm-Proxy. Er sagt, welcher Anlass zugestellt werden kann — und nur
 * diese Chips werden sichtbar. Bei Fehler, altem App-Stand oder Nicht-JSON
 * bleibt die ganze Zeile weg. Ein Anmeldefeld ohne Zustellung waere ein
 * Versprechen ins Leere.
 *
 * DIE AUSKUNFT WIRD EINMAL JE SEITE GEHOLT, nicht einmal je Aufbau: Dawn
 * ersetzt beim Variantenwechsel den ganzen Abschnitt, und unsere Zeile kommt
 * neu und wieder `hidden` zurueck. Ein MutationObserver richtet sie dann aus
 * der gemerkten Antwort neu her — ohne zweiten Netzzugriff (gleiches Muster
 * wie ap-cart-companion.js).
 *
 * WARUM DIE MENGENSTUFEN VOM SERVER KOMMEN: `qtySchwelle` und `qtyStufen`
 * stehen in lib/pricewatch/pricewatch.ts und entscheiden dort auch ueber die
 * Annahme der Anmeldung. Baute das Theme seine eigene Liste, haetten Formular
 * und Pruefung zwei Wahrheiten — und der Kunde bekaeme eine Ablehnung auf eine
 * Auswahl, die wir ihm selbst angeboten haben.
 */

(function () {
  'use strict';

  var capPromise = null;

  function capability(proxy) {
    if (capPromise) return capPromise;
    capPromise = fetch(proxy, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
    return capPromise;
  }

  /** Bestand als Zahl; -1 heisst „nicht gefuehrt" und damit „keine Aussage". */
  function stockOf(root) {
    var n = parseInt(root.getAttribute('data-stock'), 10);
    return isNaN(n) ? -1 : n;
  }

  function show(el, on) {
    if (el) el.hidden = !on;
  }

  /** Die waehlbaren Stufen: nur, was UEBER dem aktuellen Bestand liegt. */
  function stufenUeber(stufen, bestand) {
    var out = [];
    for (var i = 0; i < stufen.length; i++) {
      if (stufen[i] > bestand) out.push(stufen[i]);
    }
    return out;
  }

  function fuelleMengen(root, cap) {
    var sel = root.querySelector('[data-qty-select]');
    if (!sel) return false;
    var stufen = stufenUeber(cap.qtyStufen || [], stockOf(root));
    if (!stufen.length) return false;
    sel.innerHTML = '';
    for (var i = 0; i < stufen.length; i++) {
      var opt = document.createElement('option');
      opt.value = String(stufen[i]);
      opt.textContent = String(stufen[i]);
      sel.appendChild(opt);
    }
    return true;
  }

  /**
   * Die Zeile nach der Serverauskunft herrichten: welche Chips darf es geben?
   *
   * Streng `=== true` an jeder Stelle — alles andere (Fehler, alte App, kein
   * JSON) heisst „nicht anbieten".
   */
  function richteAus(root, cap) {
    if (!cap) return;
    var bestand = stockOf(root);
    var kaufbar = root.getAttribute('data-available') === '1';

    var chipPrice = root.querySelector('[data-chip="price"]');
    var chipStock = root.querySelector('[data-chip="stock"]');
    var chipQty = root.querySelector('[data-chip="qty"]');
    var chipAnfrage = root.querySelector('[data-chip="anfrage"]');

    show(chipPrice, cap.price === true);
    // Lieferalarm NUR fuer den ausverkauften Artikel — bei kaufbarer Ware
    // haette er nichts zu melden.
    show(chipStock, cap.stock === true && !kaufbar);

    // Mengenalarm nur fuer den kaufbaren, aber knappen Artikel. `bestand < 0`
    // ist „nicht gefuehrt": dann ist die Knappheit unbekannt und es gibt
    // nichts anzubieten.
    var schwelle = typeof cap.qtySchwelle === 'number' ? cap.qtySchwelle : 0;
    var knapp = kaufbar && bestand >= 1 && bestand <= schwelle;
    var mengenDa = knapp && cap.qty === true ? fuelleMengen(root, cap) : false;
    // Der Chip nennt KEINE Zahl mehr (Vorschau-Runde 1, 25.09.2026): der
    // Bestand steht schon neutral im Inventarblock darueber, und „Nur noch
    // 4 Stk. — mehr noetig?" war dieselbe Menge ein zweites Mal, in der
    // Verknappungsformel, die das Audit gerade entfernt hatte.
    show(chipQty, mengenDa);

    // Die Grossmengen-Anfrage braucht ZWEI Zahlen vom Server: ab wann und in welchen
    // Schritten. Fehlt eine davon (alte App-Version, kaputte Antwort), wird der Chip NICHT
    // gezeigt — derselbe Grundsatz wie ueberall hier: keine Zahl, keine Zusage. Lieber kein
    // Formular als eines, das zu einer Menge einlaedt, die der Server dann ablehnt.
    var anfrageOk =
      cap.anfrage === true &&
      typeof cap.anfrageMin === 'number' &&
      typeof cap.anfrageSchritt === 'number';
    if (anfrageOk) richteAnfrageAus(root, cap);
    show(chipAnfrage, anfrageOk);

    // Die Zeile selbst erscheint nur, wenn wenigstens EIN Chip etwas anbietet.
    var sichtbar = root.querySelectorAll('.ap-alarm__chip:not([hidden])').length;
    root.hidden = sichtbar === 0;

    // Die Kaufleiste (<sticky-atc>, assets/automotive.js) zeigt „Benachrichtigen,
    // wenn lieferbar“ nur bei sichtbarem Lieferalarm-Chip. Ihr erster Abgleich
    // laeuft mobil schon im ersten Bildschirm — VOR dieser Antwort; ohne das
    // Ereignis blieb dort ein toter „Ausverkauft“-Knopf stehen, bis der Kunde am
    // Kaufknopf vorbei und zurueck scrollte.
    var ereignis;
    try {
      ereignis = new CustomEvent('ap-alarm:ready', { bubbles: true });
    } catch (e) {
      ereignis = document.createEvent('CustomEvent');
      ereignis.initCustomEvent('ap-alarm:ready', true, false, null);
    }
    root.dispatchEvent(ereignis);
  }

  /**
   * Untergrenze und Schrittweite der Grossmengen-Anfrage aus der Serverauskunft setzen:
   * an das Zahlenfeld (min/step) UND in den Hinweistext, der die Zusage traegt.
   *
   * Der Platzhalter im Beispieltext bekommt eine Menge, die WIRKLICH durchgeht
   * (Untergrenze + drei Schritte) — ein Beispiel, das der Server ablehnen wuerde, waere
   * schlimmer als gar keines.
   */
  function richteAnfrageAus(root, cap) {
    var feld = root.querySelector('[data-form="anfrage"] [name="menge"]');
    if (feld) {
      feld.min = String(cap.anfrageMin);
      feld.step = String(cap.anfrageSchritt);
      var vorlage = feld.getAttribute('data-menge-ph');
      if (vorlage) {
        feld.placeholder = fuelle(vorlage, cap.anfrageMin + 3 * cap.anfrageSchritt, cap.anfrageSchritt);
      }
    }
    var hinweis = root.querySelector('[data-anfrage-hint]');
    if (hinweis) {
      // Vorlage aus data-vorlage (D2 TH-05 c: der Absatz ist im Server-HTML leer, damit kein
      // Platzhalter als Text dasteht); ohne Attribut wie bisher aus dem Text selbst.
      var vorlageHinweis = hinweis.getAttribute('data-vorlage') || hinweis.textContent;
      hinweis.textContent = fuelle(vorlageHinweis, cap.anfrageMin, cap.anfrageSchritt);
    }
  }

  /** `{{ count }}`/`[[count]]` und `{{ step }}`/`[[step]]` ersetzen — mit und ohne Leerzeichen,
   *  weil beide Schreibweisen in Uebersetzungen vorkommen; die eckigen Marken setzt das Theme
   *  (snippets/ap-alarm.liquid), damit im Server-HTML keine Liquid-Klammern stehen. */
  function fuelle(text, count, step) {
    return String(text)
      .replace(/\{\{\s*count\s*\}\}|\[\[\s*count\s*\]\]/g, String(count))
      .replace(/\{\{\s*step\s*\}\}|\[\[\s*step\s*\]\]/g, String(step));
  }

  function status(root, message, ok) {
    var out = root.querySelector('[data-ap-alarm-status]');
    if (!out) return;
    out.textContent = message;
    out.classList.toggle('ap-alarm__status--ok', !!ok);
  }

  /** Ein Formular auf-/zuklappen. Immer nur EINES ist offen. */
  function toggle(root, name) {
    var chips = root.querySelectorAll('.ap-alarm__chip');
    var offen = false;
    for (var i = 0; i < chips.length; i++) {
      var c = chips[i];
      var aktiv = c.getAttribute('data-chip') === name && c.getAttribute('aria-expanded') !== 'true';
      c.setAttribute('aria-expanded', aktiv ? 'true' : 'false');
      if (aktiv) offen = true;
    }
    var panels = root.querySelectorAll('[data-panel]');
    for (var j = 0; j < panels.length; j++) {
      var p = panels[j];
      p.hidden = !(offen && p.getAttribute('data-panel') === name);
      if (!p.hidden) {
        var erstes = p.querySelector('input, select, textarea');
        if (erstes) erstes.focus();
      }
    }
    // Der Einwilligungssatz gehoert zu den drei Alarm-Formularen; die Anfrage
    // ist Korrespondenz und traegt ihn nicht.
    show(root.querySelector('[data-consent]'), offen && name !== 'anfrage');
    status(root, '', false);
  }

  function absenden(root, form, name) {
    var btn = form.querySelector('button[type="submit"]');
    var url =
      name === 'anfrage' ? root.getAttribute('data-anfrage-proxy') : root.getAttribute('data-proxy');
    var ean = root.getAttribute('data-key');
    if (!url || !ean) return;

    var nutzlast = { ean: ean };
    if (name === 'anfrage') {
      nutzlast.menge = (form.querySelector('[name="menge"]') || {}).value;
      nutzlast.email = (form.querySelector('[name="email"]') || {}).value;
      nutzlast.telefon = (form.querySelector('[name="telefon"]') || {}).value;
      nutzlast.nachricht = (form.querySelector('[name="nachricht"]') || {}).value;
      nutzlast.hp = (form.querySelector('[name="hp"]') || {}).value;
    } else {
      nutzlast.kind = name;
      nutzlast.email = (form.querySelector('[name="email"]') || {}).value;
      if (name === 'qty') {
        nutzlast.wantedQty = (form.querySelector('[name="wantedQty"]') || {}).value;
      }
    }

    // Die Pflichtfelder pruefen wir selbst: `novalidate` haelt die Browser-
    // Blase weg, die auf dem Handy ueber der Buybox haengen bleibt.
    if (!nutzlast.email) {
      var mail = form.querySelector('[name="email"]');
      if (mail) mail.focus();
      return;
    }

    if (btn) btn.disabled = true;
    status(root, root.getAttribute('data-saving') || '…', true);

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(nutzlast),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        // Die Antworttexte kommen vom Server (deutsch, mit Begruendung im
        // Ablehnungsfall) — das Theme erfindet keine eigenen.
        status(root, (res && res.message) || 'Gespeichert.', !!(res && res.ok));
        if (res && res.ok) {
          form.reset();
          // Erledigt: das Formular schliesst sich, die Bestaetigung bleibt
          // stehen. Sonst steht der Kunde vor einem leeren Feld und weiss
          // nicht, ob er es nochmal ausfuellen soll.
          var chip = root.querySelector('[data-chip="' + name + '"]');
          if (chip) chip.setAttribute('aria-expanded', 'false');
          var panel = root.querySelector('[data-panel="' + name + '"]');
          if (panel) panel.hidden = true;
          show(root.querySelector('[data-consent]'), false);
        }
      })
      .catch(function () {
        status(root, root.getAttribute('data-error') || 'Das hat gerade nicht geklappt.', false);
      })
      .then(function () {
        if (btn) btn.disabled = false;
      });
  }

  /** Alle noch nicht hergerichteten Zeilen bedienen. */
  function boot() {
    var roots = document.querySelectorAll('[data-ap-alarm]:not([data-ap-alarm-ready])');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) {
      (function (root) {
        var proxy = root.getAttribute('data-proxy');
        if (!proxy) return;
        // Die Markierung steht VOR dem Abruf: sie verhindert den Doppellauf,
        // wenn der Beobachter waehrend der Antwort noch einmal feuert.
        root.setAttribute('data-ap-alarm-ready', '1');
        capability(proxy).then(function (cap) {
          richteAus(root, cap);
        });
      })(roots[i]);
    }
  }

  // Delegiert am Dokument: die Zeile wird beim Variantenwechsel ausgetauscht,
  // an ihr haengende Zuhoerer waeren dann weg.
  document.addEventListener('click', function (event) {
    var chip = event.target.closest ? event.target.closest('.ap-alarm__chip') : null;
    if (!chip) return;
    var root = chip.closest('[data-ap-alarm]');
    if (!root) return;
    event.preventDefault();
    toggle(root, chip.getAttribute('data-chip'));
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute || !form.getAttribute('data-form')) return;
    var root = form.closest('[data-ap-alarm]');
    if (!root) return;
    event.preventDefault();
    absenden(root, form, form.getAttribute('data-form'));
  });

  /* START BEI SICHTBARKEIT, BEOBACHTER NUR AM HAUPTINHALT (P1 M5): vorher boot() beim Auswerten und ein Beobachter am
     GANZEN Dokument, der bei jeder Einfuegung das Dokument abfragte (Lighthouse M0: 226 ms je PDP). Jetzt Start bei
     200 px Annaeherung bzw. im Leerlauf nach load (spaetestens 2 s); Beobachter nur am Hauptinhalt, Dokument-Abfrage
     nur, wenn eine eingefuegte Zeile dabei ist. Nach dem Laden oder ohne IntersectionObserver sofort wie bisher. */
  var gestartet = false;

  function starten() {
    if (gestartet) return;
    gestartet = true;
    boot();
  }

  function spaetStarten() {
    var nah = null;
    var los = function () {
      if (nah) nah.disconnect();
      nah = null;
      starten();
    };
    var zeilen = document.querySelectorAll('[data-ap-alarm]');
    if (zeilen.length) {
      nah = new IntersectionObserver(
        function (eintraege) {
          for (var i = 0; i < eintraege.length; i++) {
            if (eintraege[i].isIntersecting) return los();
          }
        },
        { rootMargin: '200px 0px' }
      );
      for (var z = 0; z < zeilen.length; z++) nah.observe(zeilen[z]);
    }
    var leerlauf = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(los, { timeout: 2000 });
      else window.setTimeout(los, 200);
    };
    if (document.readyState === 'complete') leerlauf();
    else window.addEventListener('load', leerlauf, { once: true });
  }

  if (document.readyState === 'complete' || typeof window.IntersectionObserver !== 'function') {
    starten();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', spaetStarten, { once: true });
  } else {
    spaetStarten();
  }

  /** Bringen die eingefuegten Knoten eine Alarm-Zeile mit? (zuerst die Knoten selbst, dann ihr Inneres) */
  function zeileDabei(liste) {
    for (var i = 0; i < liste.length; i++) {
      var hinzu = liste[i].addedNodes;
      for (var a = 0; a < hinzu.length; a++) {
        var n = hinzu[a];
        if (n.nodeType !== 1) continue;
        if (n.hasAttribute('data-ap-alarm') || (n.querySelector && n.querySelector('[data-ap-alarm]'))) return true;
      }
    }
    return false;
  }

  // Der Variantenwechsel ersetzt den Abschnitt — die neue Zeile kommt ohne
  // Markierung und wieder `hidden` zurueck und wird hier nachgezogen (vor dem Start
  // erledigt das der Start selbst).
  if (typeof MutationObserver === 'function') {
    var beobachten = function () {
      var haupt = document.getElementById('MainContent') || document.documentElement;
      new MutationObserver(function (liste) {
        if (gestartet && zeileDabei(liste)) boot();
      }).observe(haupt, {
        childList: true,
        subtree: true,
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', beobachten, { once: true });
    else beobachten();
  }
})();
